/**
 * Card "Fondos de terceros" (campañas muni/JJVV) — 2026-06.
 *
 * Trazabilidad de la plata que el barrio recauda PARA un tercero y luego
 * le transfiere: recaudado vs transferido vs pendiente (global) + desglose
 * por categoría. Esta plata toca el banco pero NO cuenta como
 * recaudación/gastos del barrio (por eso se reporta aparte).
 *
 * Presentacional (recibe `data` por prop, lo fetchea el wrapper BancoPage,
 * respetando el patrón View/wrapper del módulo). Si no hay categorías de
 * fondos de terceros (data null o sin categorías), no renderiza nada.
 */
import type { ResumenFondosTerceros } from '../../lib/api/fondos-terceros';
import { Card } from '../ui/Card';
import { Monto } from './Monto';

export function FondosTercerosCard({
  data,
}: {
  data: ResumenFondosTerceros | null;
}) {
  // Sin categorías de terceros configuradas → no se muestra nada.
  if (data === null || data.categorias.length === 0) return null;

  return (
    <Card>
      <h2 className="text-lg font-medium text-text-primary mb-1">
        Fondos de terceros
      </h2>
      <p className="text-sm text-text-secondary mb-4">
        Plata que el barrio recauda para un tercero (muni / JJVV) y le
        transfiere. Toca el banco pero no es recaudación ni gasto del barrio.
        El <strong>pendiente</strong> es lo que aún está en la cuenta a nombre
        del tercero (debería tender a cero).
      </p>

      <div className="flex flex-wrap gap-6 mb-4 text-sm">
        <div>
          <div className="text-text-muted text-xs">Recaudado</div>
          <Monto value={data.total_recaudado} className="text-base" />
        </div>
        <div>
          <div className="text-text-muted text-xs">Transferido</div>
          <Monto value={data.total_transferido} className="text-base" />
        </div>
        <div>
          <div className="text-text-muted text-xs">Pendiente</div>
          <Monto
            value={data.total_pendiente}
            className={`text-base ${
              data.total_pendiente === 0 ? 'text-emerald-400' : 'text-orange'
            }`}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/[0.08] text-left text-xs text-text-muted">
              <th className="py-2 pr-3">Categoría</th>
              <th className="py-2 pr-3">Tipo</th>
              <th className="py-2 pr-3 text-right">Recaudado</th>
              <th className="py-2 pr-3 text-right">Transferido</th>
            </tr>
          </thead>
          <tbody>
            {data.categorias.map((c) => (
              <tr key={c.categoria_id} className="border-b border-white/[0.04]">
                <td className="py-2 pr-3 text-text-primary">
                  {c.categoria_nombre}
                </td>
                <td className="py-2 pr-3 text-text-secondary">
                  {c.tipo === 'ingreso' ? 'Recaudación' : 'Transferencia'}
                </td>
                <td className="py-2 pr-3 text-right text-text-secondary">
                  {c.recaudado > 0 ? <Monto value={c.recaudado} /> : '—'}
                </td>
                <td className="py-2 pr-3 text-right text-text-secondary">
                  {c.transferido > 0 ? <Monto value={c.transferido} /> : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
